import { useState, useEffect, useRef, useCallback } from "react";
import { env } from "../../../config/env";
import { authStorage } from "../../../lib/storage/authStorage";

// Ported from Video_generator_agent's frontend/src/components/TextOverlayEditor.jsx.
// That component calls the platform directly from the browser with a bundled
// API key (fine for its own internal admin tool) — this version never talks
// to Video_generator_agent directly. It only calls gulliver's own session-keyed
// proxy routes (video_overlay_routes.py), which resolve the real platform
// job_id server-side, so the platform API key never reaches the browser.

type ElementAnimation = "fade" | "slideUp" | "pop" | "letterReveal" | "wordPunch" | "none";
type ElementDecoration = "none" | "underline" | "gradientShimmer";

interface TextElement {
  id: string;
  type: "text";
  text: string;
  x: number;
  y: number;
  fontFamily: string;
  fontSize: number;
  color: string;
  animation: ElementAnimation;
  decoration: ElementDecoration;
  opacity: number;
  rotation: number;
}

interface ShapeElement {
  id: string;
  type: "shape";
  shape: "rect" | "circle";
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  opacity: number;
  rotation: number;
  animation: ElementAnimation;
}

type SceneElement = TextElement | ShapeElement;

interface SceneInfo {
  id: string;
  durationInFrames: number;
}

interface EditState {
  scenes: SceneInfo[];
  fps: number;
  scene_texts: Record<string, string>;
  style: {
    font_family?: string;
    brand_color?: string;
    overlay_style?: string;
    overlay_animation?: string;
    overlay_decoration?: string;
  };
}

const FONTS = [
  { value: "Inter", label: "Inter — modern sans" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Poppins", label: "Poppins" },
  { value: "Cormorant Garamond", label: "Cormorant Garamond" },
];
const DEFAULT_COLOR = "#ffffff";
const DEFAULT_FONT_SIZE = 72; // matches the render canvas's px scale (1080x1920)
const CANVAS_WIDTH = 1080;
const ANIMATIONS: { value: ElementAnimation; label: string }[] = [
  { value: "fade", label: "Fade" },
  { value: "slideUp", label: "Slide up" },
  { value: "pop", label: "Pop" },
  { value: "letterReveal", label: "Letter reveal" },
  { value: "wordPunch", label: "Word punch" },
  { value: "none", label: "None" },
];
const DECORATIONS: { value: ElementDecoration; label: string }[] = [
  { value: "none", label: "None" },
  { value: "underline", label: "Underline" },
  { value: "gradientShimmer", label: "Shimmer" },
];
const VALID_ANIMATIONS = new Set(ANIMATIONS.map(a => a.value));
const VALID_DECORATIONS = new Set(DECORATIONS.map(d => d.value));

const resolveUrl = (url: string) => (url.includes("X-Amz-") ? url : `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`);
const genId = () => (crypto.randomUUID ? crypto.randomUUID() : `b${Math.random().toString(36).slice(2)}`);

function authHeaders(): HeadersInit {
  const token = authStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface Props {
  sessionId: string;
  videoUrl: string;
  onSaved: (videoUrl: string) => void;
  onCancel: () => void;
}

export default function ChatOverlayEditor({ sessionId, videoUrl, onSaved, onCancel }: Props) {
  const [scenes, setScenes] = useState<SceneInfo[]>([]);
  const [fps, setFps] = useState(30);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [elementsByScene, setElementsByScene] = useState<Record<string, SceneElement[]>>({});
  const [selected, setSelected] = useState<{ sceneId: string; elId: string } | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(300);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dragRef = useRef<{ sceneId: string; elId: string; rect: DOMRect } | null>(null);
  const resizeRef = useRef<{ sceneId: string; elId: string; centerX: number; centerY: number; rect: DOMRect } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${env.apiBaseUrl}/api/v1/media/session/${encodeURIComponent(sessionId)}/edit-state`, {
      headers: authHeaders(),
    })
      .then(r => r.json())
      .then((data: EditState) => {
        if (cancelled) return;
        setScenes(data.scenes || []);
        setFps(data.fps || 30);
        // Seed one editable text element per scene from the AI-written copy
        // already generated for this session — POST .../overlays replaces a
        // scene's overlays wholesale, so opening blank would silently drop
        // this text on save unless it's pre-populated here.
        const seeded: Record<string, SceneElement[]> = {};
        const style = data.style || {};
        for (const scene of data.scenes || []) {
          const text = (data.scene_texts || {})[scene.id];
          if (!text) continue;
          seeded[scene.id] = [{
            id: genId(), type: "text", text, x: 50, y: 50,
            fontFamily: style.font_family || FONTS[0].value,
            fontSize: DEFAULT_FONT_SIZE,
            color: style.brand_color || DEFAULT_COLOR,
            animation: VALID_ANIMATIONS.has(style.overlay_animation as ElementAnimation) ? (style.overlay_animation as ElementAnimation) : "fade",
            decoration: VALID_DECORATIONS.has(style.overlay_decoration as ElementDecoration) ? (style.overlay_decoration as ElementDecoration) : "none",
            opacity: 1, rotation: 0,
          }];
        }
        setElementsByScene(seeded);
      })
      .catch(() => { if (!cancelled) setError("Could not load scene info"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w) setCanvasWidth(w);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const sceneStarts: number[] = [];
  { let acc = 0; for (const sc of scenes) { sceneStarts.push(acc); acc += (sc.durationInFrames || 0) / fps; } }

  const seekToScene = useCallback((idx: number) => {
    setSceneIdx(idx);
    setSelected(null);
    const v = videoRef.current;
    if (v && sceneStarts[idx] != null) {
      v.pause();
      v.currentTime = sceneStarts[idx] + 0.05;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenes, fps]);

  useEffect(() => { if (scenes.length) seekToScene(0); }, [scenes]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentScene = scenes[sceneIdx];
  const currentElements = currentScene ? (elementsByScene[currentScene.id] || []) : [];
  const scale = canvasWidth / CANVAS_WIDTH;

  function addElement(el: SceneElement) {
    if (!currentScene) return;
    setElementsByScene(prev => ({ ...prev, [currentScene.id]: [...(prev[currentScene.id] || []), el] }));
    setSelected({ sceneId: currentScene.id, elId: el.id });
  }

  function insertText(x = 50, y = 50) {
    addElement({
      id: genId(), type: "text", text: "Tap to edit", x, y,
      fontFamily: FONTS[0].value, fontSize: DEFAULT_FONT_SIZE, color: DEFAULT_COLOR,
      animation: "fade", decoration: "none", opacity: 1, rotation: 0,
    });
  }

  function insertShape(shape: "rect" | "circle") {
    addElement({
      id: genId(), type: "shape", shape,
      x: 50, y: 50,
      width: shape === "circle" ? 22 : 30,
      height: shape === "circle" ? 22 : 15,
      fill: "#FFD24C", opacity: 0.6, rotation: 0, animation: "fade",
    });
  }

  function handleWrapClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target !== wrapRef.current && e.target !== videoRef.current) return;
    if (!currentScene || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    insertText(x, y);
  }

  function updateElement(sceneId: string, elId: string, patch: Partial<SceneElement>) {
    setElementsByScene(prev => ({
      ...prev,
      [sceneId]: (prev[sceneId] || []).map(e => (e.id === elId ? { ...e, ...patch } as SceneElement : e)),
    }));
  }

  function deleteElement(sceneId: string, elId: string) {
    setElementsByScene(prev => ({ ...prev, [sceneId]: (prev[sceneId] || []).filter(e => e.id !== elId) }));
    setSelected(null);
  }

  function onElPointerDown(e: React.PointerEvent, sceneId: string, elId: string) {
    e.stopPropagation();
    if (!wrapRef.current) return;
    setSelected({ sceneId, elId });
    dragRef.current = { sceneId, elId, rect: wrapRef.current.getBoundingClientRect() };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragUp);
  }

  function onDragMove(e: PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - d.rect.left) / d.rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - d.rect.top) / d.rect.height) * 100));
    updateElement(d.sceneId, d.elId, { x, y });
  }

  function onDragUp() {
    dragRef.current = null;
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragUp);
  }

  function onResizePointerDown(e: React.PointerEvent, sceneId: string, elId: string, centerX: number, centerY: number) {
    e.stopPropagation();
    if (!wrapRef.current) return;
    resizeRef.current = { sceneId, elId, centerX, centerY, rect: wrapRef.current.getBoundingClientRect() };
    window.addEventListener("pointermove", onResizeMove);
    window.addEventListener("pointerup", onResizeUp);
  }

  function onResizeMove(e: PointerEvent) {
    const r = resizeRef.current;
    if (!r) return;
    const px = ((e.clientX - r.rect.left) / r.rect.width) * 100;
    const py = ((e.clientY - r.rect.top) / r.rect.height) * 100;
    const width = Math.min(100, Math.max(4, Math.abs(px - r.centerX) * 2));
    const height = Math.min(100, Math.max(4, Math.abs(py - r.centerY) * 2));
    updateElement(r.sceneId, r.elId, { width, height });
  }

  function onResizeUp() {
    resizeRef.current = null;
    window.removeEventListener("pointermove", onResizeMove);
    window.removeEventListener("pointerup", onResizeUp);
  }

  function serializeElement(el: SceneElement) {
    if (el.type === "shape") {
      return { type: "shape", shape: el.shape, x: el.x, y: el.y, width: el.width, height: el.height, fill: el.fill, opacity: el.opacity, rotation: el.rotation, animation: el.animation };
    }
    return { type: "text", text: el.text, x: el.x, y: el.y, fontFamily: el.fontFamily, fontSize: el.fontSize, color: el.color, animation: el.animation, decoration: el.decoration, opacity: el.opacity, rotation: el.rotation };
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const sceneOverlays: Record<string, unknown[]> = {};
      for (const [sceneId, elements] of Object.entries(elementsByScene)) {
        if (elements.length) sceneOverlays[sceneId] = elements.map(serializeElement);
      }
      const r = await fetch(`${env.apiBaseUrl}/api/v1/media/session/${encodeURIComponent(sessionId)}/overlays`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ sceneOverlays }),
      });
      const j = await r.json();
      if (!r.ok) {
        const detail = (j as { detail?: { message?: string } | string }).detail;
        const msg = typeof detail === "object" ? detail?.message : detail;
        throw new Error(msg || "Save failed");
      }
      onSaved(j.video_url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const selectedEl = selected ? (elementsByScene[selected.sceneId] || []).find(e => e.id === selected.elId) : null;
  const patchSelected = (patch: Partial<SceneElement>) => selected && updateElement(selected.sceneId, selected.elId, patch);

  if (loading) {
    return <div style={{ padding: 32, textAlign: "center", color: "#8a94b4" }}>Loading…</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "32px 24px", overflowY: "auto" }}>
      <div style={{ fontSize: 12, color: "#8a94b4", textAlign: "center" }}>
        Tap the video to add text · drag to move{scenes.length > 1 ? ` · Scene ${sceneIdx + 1} / ${scenes.length}` : ""}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => insertText()} style={navBtnStyle}>+ Text</button>
        <button onClick={() => insertShape("rect")} style={navBtnStyle}>+ Rectangle</button>
        <button onClick={() => insertShape("circle")} style={navBtnStyle}>+ Circle</button>
      </div>

      <div
        ref={wrapRef}
        onClick={handleWrapClick}
        style={{ width: "100%", maxWidth: 300, aspectRatio: "9/16", position: "relative", borderRadius: 16, overflow: "hidden", background: "#000", cursor: "crosshair" }}
      >
        <video
          ref={videoRef}
          src={resolveUrl(videoUrl)}
          playsInline
          muted
          preload="auto"
          onLoadedMetadata={() => {
            // Re-apply the pending seek once duration is actually known —
            // setting currentTime before metadata loads is unreliable across
            // browsers (this was the root cause of a persistently black canvas).
            const v = videoRef.current;
            const start = sceneStarts[sceneIdx];
            if (v && start != null) v.currentTime = start + 0.05;
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
        />
        {currentElements.map(el => {
          const isSelected = selected?.elId === el.id;
          if (el.type === "shape") {
            return (
              <div
                key={el.id}
                onPointerDown={e => onElPointerDown(e, currentScene!.id, el.id)}
                style={{
                  position: "absolute", left: `${el.x}%`, top: `${el.y}%`,
                  width: `${el.width}%`, height: `${el.height}%`,
                  transform: `translate(-50%, -50%) rotate(${el.rotation || 0}deg)`,
                  background: el.fill, opacity: el.opacity ?? 1,
                  borderRadius: el.shape === "circle" ? "50%" : 4,
                  border: isSelected ? "1px dashed #FFD24C" : "1px dashed transparent",
                  cursor: "grab", touchAction: "none",
                }}
              >
                {isSelected && (
                  <div
                    onPointerDown={e => onResizePointerDown(e, currentScene!.id, el.id, el.x, el.y)}
                    style={{
                      position: "absolute", right: -7, bottom: -7, width: 14, height: 14,
                      borderRadius: "50%", background: "#FFD24C", border: "2px solid #0a0800",
                      cursor: "nwse-resize",
                    }}
                  />
                )}
              </div>
            );
          }
          return (
            <div
              key={el.id}
              onPointerDown={e => onElPointerDown(e, currentScene!.id, el.id)}
              style={{
                position: "absolute", left: `${el.x}%`, top: `${el.y}%`,
                transform: `translate(-50%, -50%) rotate(${el.rotation || 0}deg)`,
                padding: 4, cursor: "grab", userSelect: "none", touchAction: "none",
                border: isSelected ? "1px dashed #FFD24C" : "1px dashed transparent",
                fontFamily: el.fontFamily, fontSize: Math.round(el.fontSize * scale), color: el.color,
                opacity: el.opacity ?? 1,
                fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.6)", textAlign: "center",
                whiteSpace: "pre-wrap", maxWidth: "84%", lineHeight: 1.05,
                textDecoration: el.decoration === "underline" ? "underline" : "none",
              }}
            >
              {el.text}
            </div>
          );
        })}
      </div>

      {scenes.length > 1 && (
        <div style={{ display: "flex", gap: 8 }}>
          <button disabled={sceneIdx === 0} onClick={() => seekToScene(sceneIdx - 1)} style={navBtnStyle}>‹ Prev scene</button>
          <button disabled={sceneIdx >= scenes.length - 1} onClick={() => seekToScene(sceneIdx + 1)} style={navBtnStyle}>Next scene ›</button>
        </div>
      )}

      {selectedEl && selectedEl.type === "text" && (
        <div style={toolbarStyle}>
          <input
            value={selectedEl.text}
            onChange={e => patchSelected({ text: e.target.value })}
            style={{ ...toolbarInputStyle, flex: "1 1 100%" }}
          />
          <label style={sliderLabelStyle}>
            Font
            <select value={selectedEl.fontFamily} onChange={e => patchSelected({ fontFamily: e.target.value })} style={toolbarInputStyle}>
              {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </label>
          <label style={sliderLabelStyle}>
            Size
            <input
              type="number" min={20} max={200} step={4}
              value={selectedEl.fontSize}
              onChange={e => patchSelected({ fontSize: Number(e.target.value) })}
              style={{ ...toolbarInputStyle, width: 56 }}
            />
          </label>
          <label style={sliderLabelStyle}>
            Color
            <input
              type="color"
              value={selectedEl.color}
              onChange={e => patchSelected({ color: e.target.value })}
              style={colorSwatchStyle}
            />
          </label>
          <label style={sliderLabelStyle}>
            Animation
            <select value={selectedEl.animation} onChange={e => patchSelected({ animation: e.target.value as ElementAnimation })} style={toolbarInputStyle}>
              {ANIMATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </label>
          <label style={sliderLabelStyle}>
            Decoration
            <select value={selectedEl.decoration} onChange={e => patchSelected({ decoration: e.target.value as ElementDecoration })} style={toolbarInputStyle}>
              {DECORATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </label>
          <label style={sliderLabelStyle}>
            Opacity
            <input type="range" min={0} max={1} step={0.05} value={selectedEl.opacity} onChange={e => patchSelected({ opacity: Number(e.target.value) })} />
          </label>
          <label style={sliderLabelStyle}>
            Rotation
            <input type="range" min={-180} max={180} step={1} value={selectedEl.rotation} onChange={e => patchSelected({ rotation: Number(e.target.value) })} />
          </label>
          <button onClick={() => selected && deleteElement(selected.sceneId, selected.elId)} title="Delete" style={deleteBtnStyle}>×</button>
        </div>
      )}

      {selectedEl && selectedEl.type === "shape" && (
        <div style={toolbarStyle}>
          <label style={sliderLabelStyle}>
            Fill
            <input
              type="color"
              value={selectedEl.fill}
              onChange={e => patchSelected({ fill: e.target.value })}
              style={colorSwatchStyle}
            />
          </label>
          <label style={sliderLabelStyle}>
            Animation
            <select value={selectedEl.animation} onChange={e => patchSelected({ animation: e.target.value as ElementAnimation })} style={toolbarInputStyle}>
              {ANIMATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </label>
          <label style={sliderLabelStyle}>
            Opacity
            <input type="range" min={0} max={1} step={0.05} value={selectedEl.opacity} onChange={e => patchSelected({ opacity: Number(e.target.value) })} />
          </label>
          <label style={sliderLabelStyle}>
            Rotation
            <input type="range" min={-180} max={180} step={1} value={selectedEl.rotation} onChange={e => patchSelected({ rotation: Number(e.target.value) })} />
          </label>
          <button onClick={() => selected && deleteElement(selected.sceneId, selected.elId)} title="Delete" style={deleteBtnStyle}>×</button>
        </div>
      )}

      {error && <div style={{ color: "#ef4444", fontSize: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, width: "100%", maxWidth: 300 }}>
        <button onClick={onCancel} disabled={saving} style={{ ...actionBtnStyle, background: "transparent", border: "1px solid #263050", color: "#8a94b4" }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} style={{ ...actionBtnStyle, background: "linear-gradient(135deg, #FFD24C, #ff9500)", color: "#0a0800", fontWeight: 700, border: "none" }}>
          {saving ? "Rendering…" : "Save & Render"}
        </button>
      </div>
    </div>
  );
}

const navBtnStyle: React.CSSProperties = {
  cursor: "pointer", border: "1px solid #263050", background: "transparent",
  color: "#8a94b4", borderRadius: 8, padding: "6px 12px", fontSize: 12,
};
const actionBtnStyle: React.CSSProperties = {
  flex: 1, cursor: "pointer", borderRadius: 10, padding: "11px", fontSize: 13,
};
const toolbarStyle: React.CSSProperties = {
  display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
  background: "#0e1220", border: "1px solid #1e2740", borderRadius: 10, padding: 10,
  width: "100%", maxWidth: 300, boxSizing: "border-box",
};
const toolbarInputStyle: React.CSSProperties = {
  background: "#07090f", border: "1px solid #1e2740", color: "#e8ecf8",
  borderRadius: 6, padding: "6px 8px", fontSize: 11, minWidth: 0,
};
const colorSwatchStyle: React.CSSProperties = {
  width: 28, height: 28, border: "1px solid #1e2740", borderRadius: 6,
  background: "none", cursor: "pointer", padding: 0, flexShrink: 0,
};
const deleteBtnStyle: React.CSSProperties = {
  border: "1px solid #263050", background: "transparent", color: "#ef4444",
  borderRadius: 6, width: 28, height: 28, cursor: "pointer", flexShrink: 0,
};
const sliderLabelStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", fontSize: 10, color: "#4d567a",
  flex: "1 1 45%", gap: 2,
};
